import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
// Import debug tools (makes them available in browser console)
import './utils/debugSettings';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { isAuthenticated } from './services/authService';
import CategoriesPage from './pages/inventory/CategoriesPage';
import UnitsPage from './pages/inventory/UnitsPage';
import ProductsPage from './pages/inventory/ProductsPage';
import QuickProductEntryPage from './pages/inventory/QuickProductEntryPage';
import ProductImport from './pages/inventory/ProductImport';
import BarcodeGeneratePage from './pages/inventory/BarcodeGeneratePage';
import LocationsPage from './pages/inventory/LocationsPage';
import LocationUsersPage from './pages/inventory/LocationUsersPage';
import StockTransferPage from './pages/inventory/StockTransferPage';
import CreateStockTransferPage from './pages/inventory/CreateStockTransferPage';
import TaxesPage from './pages/inventory/TaxesPage';
import PhysicalStockUpdate from './pages/inventory/PhysicalStockUpdate';
import RepackingManagementPage from './pages/inventory/RepackingManagementPage';
import PhysicalStockEntry from './pages/inventory/PhysicalStockEntry';
import ViewPhysicalStockEntry from './pages/inventory/ViewPhysicalStockEntry';
import InventoryReports from './pages/InventoryReports';
import Sales from './components/sales';
import Purchases from './components/purchases';
import Reports from './components/reports';
import People from './components/people';
import CustomerLedger from './pages/reports/CustomerLedger';
import CustomerLedgerHistoryPage from './pages/reports/CustomerLedgerHistoryPage';
import SupplierLedger from './pages/reports/SupplierLedger';
import SupplierLedgerHistoryPage from './pages/reports/SupplierLedgerHistoryPage';
import PrivilegesPage from './pages/PrivilegesPage';
import RolePrivilegesPage from './pages/RolePrivilegesPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import NotificationsPage from './pages/NotificationsPage';
import CompanySettings from './pages/settings/CompanySettings';
import WebsiteSettings from './pages/settings/WebsiteSettings';
import PrintSettingsPage from './pages/settings/PrintSettingsPage';
import PrintFormatCustomizer from './pages/settings/PrintFormatCustomizer';
import LabelSettingsPage from './pages/settings/LabelSettingsPage';
import StorefrontTestPage from './pages/ecommerce/StorefrontTestPage';
import CustomerProfilePage from './pages/ecommerce/CustomerProfilePage';
import CustomerOrdersPage from './pages/ecommerce/CustomerOrdersPage';
import CartCheckoutPage from './pages/ecommerce/CartCheckoutPage';
import ExpenseEntryPage from './pages/expenses/ExpenseEntryPage';
import ExpenseViewPage from './pages/expenses/ExpenseViewPage';
import ProfilePage from './pages/ProfilePage';
import PrivilegedRoute from './routes/PrivilegedRoute';
import { NotificationProvider } from './context/NotificationContext';
import { CurrencyProvider } from './context/CurrencyContext';
import OfflineSyncProvider from './components/offline/OfflineSyncProvider';

// SaaS Components
import TenantRegistrationPage from './pages/saas/TenantRegistrationPage';
import SubscriptionDashboard from './pages/saas/SubscriptionDashboard';
import PaymentPage from './pages/PaymentPage';

// Settings Components
import RegionalSettings from './pages/settings/RegionalSettings';
import LoyaltySettingsPage from './pages/settings/LoyaltySettingsPage';
import PageFieldSettingsPage from './pages/settings/PageFieldSettingsPage';
import ActivityLogPage from './pages/settings/ActivityLogPage';
import WhatsAppConfigurationPage from './pages/settings/WhatsAppConfigurationPage';

// Super Admin Components
import SuperAdminLogin from './superadmin/pages/SuperAdminLogin';
import SuperAdminDashboard from './superadmin/pages/SuperAdminDashboard';
import ClientsPage from './superadmin/pages/ClientsPage';
import ActivateSubscriptionPage from './superadmin/pages/ActivateSubscriptionPage';
import SubscriptionsPage from './superadmin/pages/SubscriptionsPage';
import CreateSubscriptionPage from './superadmin/pages/CreateSubscriptionPage';
import EditSubscriptionPage from './superadmin/pages/EditSubscriptionPage';
import AddonDefinitionsPage from './superadmin/pages/AddonDefinitionsPage';
import SettingsPage from './superadmin/pages/SettingsPage';
import ProtectedSuperAdminRoute from './superadmin/routes/ProtectedSuperAdminRoute';
import { isSuperAdminAuthenticated } from './superadmin/services/superAdminAuthService';

// In Electron (file://) the pathname is the file path; use HashRouter so routes use #/path
const Router = window.location?.protocol === 'file:' ? HashRouter : BrowserRouter;

/**
 * Protected route component to prevent unauthorized access
 * @param {Object} props Component props
 * @param {JSX.Element} props.children Child components to render if authenticated
 * @returns {JSX.Element} Protected route
 */
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/" replace />;
};

/**
 * Route component for login page that redirects to dashboard if already authenticated
 * @param {Object} props Component props
 * @returns {JSX.Element} Login page or redirect
 */
const LoginRoute = () => {
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  if (isAuthenticated()) {
    // CRITICAL: Do NOT hardcode dashboard access here
    // Dashboard access must come from backend privileges via syncPrivilegesFromBackend
    // The Layout component will sync privileges from backend on mount
    // This ensures privileges are controlled by backend, not hardcoded in frontend

    return <Navigate to={from} replace />;
  }
  return <LoginPage />;
};

// Default theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

/**
 * Main application component
 * @returns {JSX.Element} The application
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <CurrencyProvider>
          <NotificationProvider>
            <Router>
              <OfflineSyncProvider>
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
              <Routes>
                <Route path="/" element={<LoginRoute />} />
                <Route path="/register" element={<TenantRegistrationPage />} />
                <Route path="/payment" element={<PaymentPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />

                {/* Super Admin Routes */}
                <Route
                  path="/superadmin/login"
                  element={
                    isSuperAdminAuthenticated() ? (
                      <Navigate to="/superadmin/dashboard" replace />
                    ) : (
                      <SuperAdminLogin />
                    )
                  }
                />
                <Route
                  path="/superadmin/dashboard"
                  element={
                    <ProtectedSuperAdminRoute>
                      <SuperAdminDashboard />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/clients"
                  element={
                    <ProtectedSuperAdminRoute>
                      <ClientsPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/subscriptions"
                  element={
                    <ProtectedSuperAdminRoute>
                      <SubscriptionsPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/clients/:companyId/activate-subscription"
                  element={
                    <ProtectedSuperAdminRoute>
                      <ActivateSubscriptionPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/subscriptions/create"
                  element={
                    <ProtectedSuperAdminRoute>
                      <CreateSubscriptionPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/subscriptions/edit/:planId"
                  element={
                    <ProtectedSuperAdminRoute>
                      <EditSubscriptionPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/subscriptions/addon-definitions"
                  element={
                    <ProtectedSuperAdminRoute>
                      <AddonDefinitionsPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route
                  path="/superadmin/settings"
                  element={
                    <ProtectedSuperAdminRoute>
                      <SettingsPage />
                    </ProtectedSuperAdminRoute>
                  }
                />
                <Route path="/dashboard" element={
                  <PrivilegedRoute path="/dashboard">
                    <DashboardPage />
                  </PrivilegedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/dashboard" element={
                  <PrivilegedRoute path="/admin/dashboard">
                    <DashboardPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/categories" element={
                  <PrivilegedRoute path="/inventory/categories">
                    <CategoriesPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/units" element={
                  <PrivilegedRoute path="/inventory/units">
                    <UnitsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/products" element={
                  <PrivilegedRoute path="/inventory/products">
                    <ProductsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/quick-product-entry" element={
                  <PrivilegedRoute path="/inventory/quick-product-entry">
                    <QuickProductEntryPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/import-products" element={
                  <PrivilegedRoute path="/inventory/import-products">
                    <ProductImport />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/barcode-generate" element={
                  <PrivilegedRoute path="/inventory/barcode-generate">
                    <BarcodeGeneratePage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/taxes" element={
                  <PrivilegedRoute path="/inventory/taxes">
                    <TaxesPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/locations" element={
                  <PrivilegedRoute path="/inventory/locations">
                    <LocationsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/location-users" element={
                  <PrivilegedRoute path="/inventory/location-users">
                    <LocationUsersPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/stock-transfer" element={
                  <PrivilegedRoute path="/inventory/stock-transfer">
                    <StockTransferPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/stock-transfer/create" element={
                  <PrivilegedRoute path="/inventory/stock-transfer/create">
                    <CreateStockTransferPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/physical-stock-update" element={
                  <PrivilegedRoute path="/inventory/physical-stock-update">
                    <PhysicalStockUpdate />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/repacking" element={
                  <PrivilegedRoute path="/inventory/repacking">
                    <RepackingManagementPage />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/physical-stock-entry" element={
                  <PrivilegedRoute path="/inventory/physical-stock-entry">
                    <PhysicalStockEntry />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/view-physical-stock-entry" element={
                  <PrivilegedRoute path="/inventory/view-physical-stock-entry">
                    <ViewPhysicalStockEntry />
                  </PrivilegedRoute>
                } />
                <Route path="/inventory/reports" element={
                  <PrivilegedRoute path="/inventory/reports">
                    <InventoryReports />
                  </PrivilegedRoute>
                } />
                <Route path="/sales/*" element={
                  <PrivilegedRoute path="/sales/*">
                    <Sales />
                  </PrivilegedRoute>
                } />
                <Route path="/purchases/*" element={
                  <PrivilegedRoute path="/purchases/*">
                    <Purchases />
                  </PrivilegedRoute>
                } />
                <Route path="/reports/*" element={
                  <PrivilegedRoute path="/reports/*">
                    <Reports />
                  </PrivilegedRoute>
                } />
                <Route path="/expenses/entry" element={
                  <PrivilegedRoute path="/expenses/entry">
                    <ExpenseEntryPage />
                  </PrivilegedRoute>
                } />
                <Route path="/expenses/view" element={
                  <PrivilegedRoute path="/expenses/view">
                    <ExpenseViewPage />
                  </PrivilegedRoute>
                } />
                <Route path="/people/*" element={
                  <PrivilegedRoute path="/people/*">
                    <People />
                  </PrivilegedRoute>
                } />
                <Route path="/privileges" element={
                  <PrivilegedRoute path="/privileges">
                    <PrivilegesPage />
                  </PrivilegedRoute>
                } />
                <Route path="/role-privileges" element={
                  <PrivilegedRoute path="/role-privileges">
                    <RolePrivilegesPage />
                  </PrivilegedRoute>
                } />
                <Route path="/customer-ledger/:customerId" element={
                  <PrivilegedRoute path="/customer-ledger/:customerId">
                    <CustomerLedgerHistoryPage />
                  </PrivilegedRoute>
                } />
                <Route path="/customer-credit-ledger/:customerId" element={
                  <PrivilegedRoute path="/customer-credit-ledger/:customerId">
                    <CustomerLedger />
                  </PrivilegedRoute>
                } />
                <Route path="/supplier-ledger/:supplierId" element={
                  <PrivilegedRoute path="/supplier-ledger/:supplierId">
                    <SupplierLedgerHistoryPage />
                  </PrivilegedRoute>
                } />
                <Route path="/supplier-credit-ledger/:supplierId" element={
                  <PrivilegedRoute path="/supplier-credit-ledger/:supplierId">
                    <SupplierLedger />
                  </PrivilegedRoute>
                } />
                <Route path="/notifications" element={
                  <PrivilegedRoute path="/notifications">
                    <NotificationsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/company" element={
                  <PrivilegedRoute path="/settings/company">
                    <CompanySettings />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/website" element={
                  <PrivilegedRoute path="/settings/website">
                    <WebsiteSettings />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/print" element={
                  <PrivilegedRoute path="/settings/print">
                    <PrintSettingsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/label-settings" element={
                  <PrivilegedRoute path="/settings/label-settings">
                    <LabelSettingsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/print/customize" element={
                  <PrivilegedRoute path="/settings/print">
                    <PrintFormatCustomizer />
                  </PrivilegedRoute>
                } />
                <Route path="/settings" element={
                  <PrivilegedRoute path="/settings">
                    <DashboardPage />
                  </PrivilegedRoute>
                } />
                <Route path="/subscription" element={
                  <PrivilegedRoute path="/subscription">
                    <SubscriptionDashboard />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/regional" element={
                  <PrivilegedRoute path="/settings/regional">
                    <RegionalSettings />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/loyalty" element={
                  <PrivilegedRoute path="/settings/loyalty">
                    <LoyaltySettingsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/page-fields" element={
                  <PrivilegedRoute path="/settings/page-fields">
                    <PageFieldSettingsPage />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/activity-log" element={
                  <PrivilegedRoute path="/settings/activity-log">
                    <ActivityLogPage />
                  </PrivilegedRoute>
                } />
                <Route path="/settings/whatsapp" element={
                  <PrivilegedRoute path="/settings/whatsapp">
                    <WhatsAppConfigurationPage />
                  </PrivilegedRoute>
                } />

                {/* Public Ecommerce Website Routes */}
                <Route path="/:websitePath/profile" element={<CustomerProfilePage />} />
                <Route path="/:websitePath/orders" element={<CustomerOrdersPage />} />
                <Route path="/:websitePath/cart" element={<CartCheckoutPage />} />
                <Route path="/:websitePath" element={<StorefrontTestPage />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </OfflineSyncProvider>
            </Router>
          </NotificationProvider>
        </CurrencyProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
